import {
  Menu,
  MenuItem,
  FormControlLabel,
  Divider,
  Grid2,
  IconButton,
  Tooltip,
  Checkbox,
  Collapse,
} from "@mui/material";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialTypography from "../../../../components/MaterialTypography";
import { ExpandMore, FilterAltOff } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../../../context/popup";
import { fetchData } from "../../../../services";
import { useLoading } from "../../../../components/Loading";
import DropDownConfig from "../../../Utils/DropDownConfig";

const ExternalApiRegistryFilterDialog = ({
  anchorEl,
  open,
  onClose,
  filterData,
  setFilterData,
  onApply,
}) => {
  /* ===================== STYLES ===================== */

  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2px 6px",
    background: "#e9ecf5",
    borderRadius: "6px",
    cursor: "pointer",
    mb: 0.5,
  };

  const menuItemStyle = {
    borderRadius: "6px",
    my: 0.2,
    mx: 0.5,
    py: 0.3,
    "&:hover": { backgroundColor: "#dfe6ff" },
  };

  const labelStyle = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
  };

  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  /* ===================== STATE ===================== */

  const [openActive, setOpenActive] = useState(false);
  const [openApiRegistry, setOpenApiRegistry] = useState(false);
  const [openEnvironment, setOpenEnvironment] = useState(false);
  const [openProtocol, setOpenProtocol] = useState(false);
  const [openAuthType, setOpenAuthType] = useState(false);

  const [apiRegistryOptions, setApiRegistryOptions] = useState([]);
  const [environmentOptions, setEnvironmentOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [authTypeOptions, setAuthTypeOptions] = useState([]);

  /* ===================== FETCH DROPDOWNS ===================== */

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        setCircleLoading(true);
        const results = await Promise.allSettled([
          fetchData(`/api-engine/api-registry?dropdown=true`),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiEnvironment}`),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiProtocol}`),
          fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiAuthType}`),
        ]);

        const [apiRegistryRes, environmentRes, protocolRes, authTypeRes] =
          results;

        if (
          apiRegistryRes.status === "fulfilled" &&
          apiRegistryRes.value?.success
        ) {
          setApiRegistryOptions(
            apiRegistryRes.value.data?.items?.map((i) => ({
              label: i.name,
              value: i.id,
            })) || []
          );
        }

        if (environmentRes.status === "fulfilled" && environmentRes.value?.success) {
          setEnvironmentOptions(
            environmentRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
        }

        if (protocolRes.status === "fulfilled" && protocolRes.value?.success) {
          setProtocolOptions(
            protocolRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
        }

        if (authTypeRes.status === "fulfilled" && authTypeRes.value?.success) {
          setAuthTypeOptions(
            authTypeRes.value.data?.items?.map((i) => ({
              label: i.keyName,
              value: i.id,
            })) || []
          );
        }
      } catch {
        showSnackbar("Failed to load filters", "error");
      }finally{
        setCircleLoading(false);
      }
    };
    if(open){
      fetchDropdowns();
    }else{
      return;
    }
  }, [open]);

  /* ===================== HANDLERS ===================== */

  const handleChange = ({ target: { name, value } }) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onClearFilters = () => {
    setFilterData({
      isActive: "",
      apiRegistryId: "",
      environmentId: "",
      protocolId: "",
      authTypeId: "",
    });
  };

  /* ===================== UI ===================== */

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      PaperProps={{
        elevation: 3,
        sx: { width: 260, p: 1.5 },
      }}
    >
      {/* ===================== HEADER ===================== */}
      <MaterialBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MaterialTypography variant="body2" fontWeight="bold">
          API Filters
        </MaterialTypography>

        <Tooltip title="Clear Filters">
          <IconButton
            size="small"
            onClick={onClearFilters}
            sx={{ color: "#c00" }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
      </MaterialBox>

      {/* ===================== CONTENT ===================== */}
      <MaterialBox mt={1} sx={{ maxHeight: 300, overflowY: "auto" }}>
        <Grid2 container spacing={1}>
          {/* ===== ACTIVE STATUS ===== */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenActive(!openActive)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Active Status
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openActive ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </MaterialBox>

              <Collapse in={openActive}>
                {[true, false].map((val) => (
                  <MenuItem
                    key={String(val)}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: { name: "isActive", value: val },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox checked={filterData.isActive === val} />
                      }
                      label={val ? "Active" : "Inactive"}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          {/* ===== API REGISTRY ===== */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenApiRegistry(!openApiRegistry)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  API Registry
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openApiRegistry
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </MaterialBox>

              <Collapse in={openApiRegistry}>
                {apiRegistryOptions.map((item) => (
                  <MenuItem
                    key={item.value}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: {
                          name: "apiRegistryId",
                          value: item.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterData.apiRegistryId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          {/* ===== ENVIRONMENT ===== */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenEnvironment(!openEnvironment)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Environment
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openEnvironment
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </MaterialBox>

              <Collapse in={openEnvironment}>
                {environmentOptions.map((item) => (
                  <MenuItem
                    key={item.value}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: {
                          name: "environmentId",
                          value: item.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterData.environmentId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          {/* ===== PROTOCOL ===== */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenProtocol(!openProtocol)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Protocol
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openProtocol ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </MaterialBox>

              <Collapse in={openProtocol}>
                {protocolOptions.map((item) => (
                  <MenuItem
                    key={item.value}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: {
                          name: "protocolId",
                          value: item.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterData.protocolId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>

          {/* ===== AUTH TYPE ===== */}
          <Grid2 size={{ xs: 12 }}>
            <MaterialBox>
              <MaterialBox
                sx={sectionHeader}
                onClick={() => setOpenAuthType(!openAuthType)}
              >
                <MaterialTypography variant="body3" fontWeight="bold">
                  Auth Type
                </MaterialTypography>
                <ExpandMore
                  sx={{
                    transform: openAuthType ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </MaterialBox>

              <Collapse in={openAuthType}>
                {authTypeOptions.map((item) => (
                  <MenuItem
                    key={item.value}
                    sx={menuItemStyle}
                    onClick={() =>
                      handleChange({
                        target: {
                          name: "authTypeId",
                          value: item.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterData.authTypeId === item.value}
                        />
                      }
                      label={item.label}
                      sx={labelStyle}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </MaterialBox>
          </Grid2>
        </Grid2>
      </MaterialBox>

      <Divider sx={{ my: 1 }} />

      {/* ===================== APPLY ===================== */}
      <MenuItem
        onClick={onApply}
        disableRipple
        sx={{
          padding: "6px 16px",
          width: "fit-content",
          minWidth: "0px",
          mx: "auto",
          borderRadius: "6px",
          backgroundColor: "#bcc7e9",
          color: "white",
          display: "flex",
          justifyContent: "center",
          transition: "0.2s ease",

          "&:hover": {
            backgroundColor: "#93a6e8",
          },
        }}
      >
        <MaterialTypography variant="button" fontWeight="bold">
          APPLY FILTER
        </MaterialTypography>
      </MenuItem>
    </Menu>
  );
};

export default ExternalApiRegistryFilterDialog;
